import { isLoggedInAtom, userInfoAtom } from 'app/atoms/auth';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ManagementTile } from 'app/components/management/title';
import { TopLoadingIndicator, useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useAllFavourites, useAllListings } from 'app/network/listing';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import { ListingType } from 'app/types/listing';
import { LoanType } from 'app/types/loan';
import type { NavigationScreen } from 'app/types/navigation';
import { DynamicFeatureFlag, FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { usePageTitle } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl } from 'react-native-gesture-handler';
import { withOptionalMetrics } from '../../utils/performance/with-metrics';
import type * as management from './navigator';
import { BonificationPrompt } from '../billboard/all-listings/components/bonification-banner';
import { getMyListingsCountFilters } from './my-listings';
import { useUser } from 'app/network/user';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArchive, IconChevronRight, IconHeart } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { UpcomingActivity } from 'app/screens/billboard/landing-page/components/upcoming-activity';
import { BonifiableListingsCountSuffix } from './components/bonifiable-listings-count-suffix';
const theme = createDSTheme();

type ActionDef = {
  [R in keyof management.Parameters]: {
    icon: ReactNode;
    route: R;
    routeParams?: management.Parameters[R];
    title: string;
    suffix?: ReactNode;
  };
}[keyof management.Parameters];

const countQueryOptions = {
  throwOnError: true,
  populateListingDetailCache: false,
} as const;

const ManagementOverviewScreenComponent: NavigationScreen<'management-overview'> = ({ navigation }) => {
  const { t } = useTranslation();
  const actionColor = theme.colors['surface.tertiary-neutral'];
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const user = useAtomValue(userInfoAtom);
  const { data: currentUser, isPending: isUserPending, refetch: refetchUser } = useUser();

  const setPageTitle = usePageTitle('management');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const canPublish = isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);
  const canViewActivitySummary =
    !isUserPending && isFeatureEnabled(DynamicFeatureFlag.ActivitySummary, currentUser?.featureFlags);

  const [deferSecondaryQueries, setDeferSecondaryQueries] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (!cancelled) setDeferSecondaryQueries(true);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, []);

  const offersQuery = useAllListings(
    getMyListingsCountFilters(isLoggedIn, user?.id, ListingType.offer),
    'ManagementOverview-Offers',
    countQueryOptions,
  );
  const { data: offers, refetch: refetchOffers, isPending: isOffersPending } = offersQuery;
  const numberOfListingsToBonify = offers?.pages[0]?.numberOfBonifiableListings ?? 0;

  const requestsQuery = useAllListings(
    getMyListingsCountFilters(isLoggedIn, user?.id, ListingType.request),
    'ManagementOverview-Requests',
    { ...countQueryOptions, enabled: deferSecondaryQueries },
  );
  const { data: requests, refetch: refetchRequests, isPending: isRequestsPending } = requestsQuery;

  const favouritesQuery = useAllFavourites({ throwOnError: true, enabled: deferSecondaryQueries });
  const { data: favourites, refetch: refetchFavourites, isPending: isFavouritesPending } = favouritesQuery;

  const lendingLoansQuery = useAllLoans(LoanFilterKey.Management, LoanType.Lending);
  const { data: lendings, refetch: refetchLending, isPending: isLendingPending } = lendingLoansQuery;

  const borrowingLoansQuery = useAllLoans(LoanFilterKey.Management, LoanType.Borrowing);
  const { data: borrowings, refetch: refetchBorrowing, isPending: isBorrowingPending } = borrowingLoansQuery;

  const pagePadding = usePagePadding();

  const publicationActions: ActionDef[] = canPublish
    ? [
        {
          icon: (
            <Column
              alignItems="center"
              justifyContent="center"
              style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: '#E4DFFF' }}
            >
              <Text fontSize={26}>🫴</Text>
            </Column>
          ),
          route: 'my-listings',
          routeParams: { type: ListingType.offer },
          title: t('title.offer', { ns: 'my-listings' }),
          suffix: (
            <BonifiableListingsCountSuffix bonifiableCount={numberOfListingsToBonify} count={offers?.pages[0]?.total} />
          ),
        },
        {
          icon: (
            <Column
              alignItems="center"
              justifyContent="center"
              style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: '#E4DFFF' }}
            >
              <Text fontSize={26}>👋</Text>
            </Column>
          ),
          route: 'my-listings',
          routeParams: { type: ListingType.request },
          title: t('title.request', { ns: 'my-listings' }),
          suffix: (
            <>
              <Column justifyContent="center" width={40}>
                <Text color="text.tertiary" fontSize={16} fontWeight="400">
                  {requests?.pages[0]?.total ?? '-'}
                </Text>
              </Column>
              <Column>
                <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
              </Column>
            </>
          ),
        },
      ]
    : [];

  const moreOptionsActions: ActionDef[] = [
    {
      icon: <IconHeart strokeWidth={ICON_STROKE_WIDTH} size={32} />,
      route: 'favourites',
      title: t('title', { ns: 'favourites' }),
      suffix: (
        <>
          <Column justifyContent="center" width={40}>
            <Text color="text.tertiary" fontSize={16} fontWeight="400">
              {favourites?.length ?? '-'}
            </Text>
          </Column>
          <Column>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
        </>
      ),
    },
    {
      icon: <IconArchive strokeWidth={ICON_STROKE_WIDTH} size={32} />,
      route: 'past-loans',
      title: t('past.title', { ns: 'loans' }),
    },
  ];

  const [lendingTileHeight, setLendingTileHeight] = useState(0);
  const [borrowingTileHeight, setBorrowingTileHeight] = useState(0);
  const tileHeight = Math.max(lendingTileHeight, borrowingTileHeight) || undefined;
  const onLendingLayout = (e: { nativeEvent: { layout: { height: number } } }) =>
    setLendingTileHeight(e.nativeEvent.layout.height);
  const onBorrowingLayout = (e: { nativeEvent: { layout: { height: number } } }) =>
    setBorrowingTileHeight(e.nativeEvent.layout.height);

  const loadingState = useTopLoadingIndicator([
    lendingLoansQuery,
    borrowingLoansQuery,
    offersQuery,
    ...(deferSecondaryQueries ? [favouritesQuery, requestsQuery] : []),
  ]);

  useRegisterLoading(isLendingPending, 'ManagementOverviewScreen-lendings');
  useRegisterLoading(isBorrowingPending, 'ManagementOverviewScreen-borrowings');
  useRegisterLoading(isOffersPending, 'ManagementOverviewScreen-offers');
  useRegisterLoading(isRequestsPending, 'ManagementOverviewScreen-requests');
  useRegisterLoading(isFavouritesPending, 'ManagementOverviewScreen-favourites');

  return (
    <>
      <TopLoadingIndicator isLoading={loadingState.isLoading} />
      <Page
        testID="management-overview-screen"
        paddingStyle={{ ...pagePadding }}
        refreshControl={
          <RefreshControl
            onRefresh={() =>
              void Promise.all([
                refetchUser(),
                refetchLending(),
                refetchBorrowing(),
                refetchRequests(),
                refetchOffers(),
                refetchFavourites(),
              ])
            }
            refreshing={false}
          />
        }
      >
        <Column flex={1} gap="spacing.general.x5">
          {canViewActivitySummary ? (
            <UpcomingActivity
              extraPadding={pagePadding.paddingLeft}
              borrowings={borrowings ?? []}
              isLoading={isLendingPending || isBorrowingPending}
              lendings={lendings ?? []}
              showWindowFilters
              timeWindow="nextThreeDays"
              variant="small"
            />
          ) : null}

          <Column gap="spacing.general.x4">
            <Text fontWeight="700" variant="bodyLarge">
              {t('ongoing', { ns: 'management' })}
            </Text>
            <Row flex={1} gap="spacing.general.x4">
              <ManagementTile
                fillSpace
                height={tileHeight}
                loans={lendings}
                navigation={navigation}
                onLayout={onLendingLayout}
                type={LoanType.Lending}
              />
              <ManagementTile
                fillSpace
                height={tileHeight}
                loans={borrowings}
                navigation={navigation}
                onLayout={onBorrowingLayout}
                type={LoanType.Borrowing}
              />
            </Row>
          </Column>

          {publicationActions.length > 0 && (
            <Column gap="spacing.general.x4">
              <Text fontWeight="700" variant="bodyLarge">
                {t('publications', { ns: 'management' })}
              </Text>
              <BonificationPrompt />
              {publicationActions.map((action) => (
                <Button
                  key={action.title}
                  testID={
                    action.route === 'my-listings' && action.routeParams?.type === ListingType.offer
                      ? 'management-my-offers-link'
                      : undefined
                  }
                  onPress={() =>
                    // @ts-expect-error tuple union thing?
                    navigation.navigate(action.route, action.routeParams, { pop: true })
                  }
                  prefix={action.icon}
                  style={{
                    marginLeft: -40,
                    paddingLeft: 40,
                    backgroundColor: actionColor,
                  }}
                  suffix={action.suffix ?? <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
                >
                  <Text fontWeight="700" style={{ flex: 1 }}>
                    {action.title}
                  </Text>
                </Button>
              ))}
            </Column>
          )}

          <Column gap="spacing.general.x4">
            <Text fontWeight="700" variant="bodyLarge">
              {t('moreOptions', { ns: 'management' })}
            </Text>
            {moreOptionsActions.map((action) => (
              <Button
                key={action.title}
                onPress={() =>
                  // @ts-expect-error tuple union thing?
                  navigation.navigate(action.route, action.routeParams, { pop: true })
                }
                prefix={action.icon}
                style={{
                  marginLeft: -40,
                  paddingLeft: 40,
                }}
                suffix={action.suffix ?? <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
              >
                <Text fontWeight="600" style={{ flex: 1 }}>
                  {action.title}
                </Text>
              </Button>
            ))}
          </Column>
        </Column>
      </Page>
    </>
  );
};

export const ManagementOverviewScreen: NavigationScreen<'management-overview'> = withOptionalMetrics(
  'ManagementOverviewScreen',
  withErrorBoundary(ManagementOverviewScreenComponent),
);
